import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { AUTH_ERRORS } from '../components/auth/authConfig';
import '../assets/styles/Login.css';

const Login = () => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [currentCognitoUser, setCurrentCognitoUser] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { login, error: authError, setKeepMeLoggedIn } = useAuth();
  
  // Get the path we should navigate to after successful login
  const from = location.state?.from?.pathname || "/";

  // Check if there's a message in the navigation state (e.g., from signup confirmation)
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location.state]);
  
  // Display errors from the auth context
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use the enhanced login from our improved AuthProvider
      const result = await login(email, password, rememberMe);
      
      // Check if new password is required (first login)
      if (result?.requiresNewPassword) {
        setCurrentCognitoUser(result.cognitoUser);
        setShowNewPasswordForm(true);
        setIsLoading(false);
        return;
      }
      
      // Update the "remember me" setting
      setKeepMeLoggedIn(rememberMe);
      
      // Add delay before navigation to allow auth state to fully update
      setTimeout(() => {
        // Successful login, navigate to the requested page or home
        navigate(from, { replace: true });
      }, 500);

      // Client initialization errors are not fatal
      if (result?.clientInitError) {
        console.warn("Login successful but there was an issue initializing AWS clients.");
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Provide more specific error messages
      if (err.message?.includes('network')) {
        setError(AUTH_ERRORS.network);
      } else if (err.message?.includes('incorrect') || err.message?.includes('Incorrect')) {
        setError(AUTH_ERRORS.invalidCredentials);
      } else {
        setError(err.message || AUTH_ERRORS.generalError);
      }
      
      setIsLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    if (newPassword !== confirmNewPassword) {
      setError(AUTH_ERRORS.passwordMismatch);
      setIsLoading(false);
      return;
    }
    
    try {
      // Call completeNewPasswordChallenge on the Cognito user
      await new Promise((resolve, reject) => {
        currentCognitoUser.completeNewPasswordChallenge(newPassword, {}, {
          onSuccess: () => resolve(),
          onFailure: (err) => reject(err)
        });
      });
      
      // After successfully setting new password, log in with the new credentials
      await login(email, newPassword, rememberMe);
      
      // Add delay before navigation to allow auth state to fully update
      setTimeout(() => {
        // Successful login, navigate to the requested page or home
        navigate(from, { replace: true });
      }, 500);
    } catch (err) {
      console.error('New password error:', err);
      
      if (err.message?.includes('password') || err.message?.includes('Password')) {
        setError(AUTH_ERRORS.passwordRequirements);
      } else {
        setError(AUTH_ERRORS.generalError);
      }
      
      setIsLoading(false);
    }
  };

  // Render the new password form if required
  if (showNewPasswordForm) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="auth-title">
            <h2>AI Maths Tutor</h2>
          </div>
          <h3>Set New Password</h3>
          <p className="info-message">You need to set a new password for your first login.</p>
          
          <form onSubmit={handleNewPasswordSubmit}>
            <div className="form-group">
              <label htmlFor="new-password">New Password</label>
              <input
                type="password"
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
              <small className="password-hint">
                Password must contain at least 8 characters, including uppercase, lowercase, number, and special character.
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <input
                type="password"
                id="confirm-password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? 'Setting Password...' : 'Set New Password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Regular login form
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="auth-title">
          <h2>AI Maths Tutor</h2>
        </div>
        <h3>Log In</h3>
        
        {message && <div className="success-message">{message}</div>}
        
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className="forgot-password">
              <Link to="/reset-password">Forgot Password?</Link>
            </div>
          </div>
          
          <div className="form-group checkbox">
            <input
              type="checkbox"
              id="remember-me"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="remember-me">Keep me signed in</label>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? 'Logging In...' : 'Log In'}
          </button>
          
          <div className="signup-link">
            Don't have an account? <Link to="/signup">Sign up</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;